import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BellAlertIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Avatar from '@/components/Avatar';
import { useToast } from '@/components/Toast';
import { eventsService } from '@/services/events.service';
import type { RsvpCounts } from './RsvpSummary';

export interface EventRsvp {
  id: string;
  user_id: string;
  user_name: string;
  status: 'yes' | 'maybe' | 'no' | 'not_responded';
}

/** Deterministic avatar background per user so the face wall feels alive. */
const AVATAR_BGS = [
  'bg-sky-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-violet-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-orange-500',
];

function avatarBg(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  return AVATAR_BGS[Math.abs(hash) % AVATAR_BGS.length];
}

function firstName(name: string): string {
  return name.split(/\s+/)[0] || name;
}

function FaceGrid({
  people,
  compact,
  muted,
}: {
  people: EventRsvp[];
  compact?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={clsx(
        'grid gap-x-2 gap-y-3',
        compact ? 'grid-cols-4' : 'grid-cols-4 sm:grid-cols-6'
      )}
    >
      {people.map((p) => (
        <div key={p.id} className="flex min-w-0 flex-col items-center" title={p.user_name}>
          <Avatar
            name={p.user_name}
            sizeClass="h-9 w-9"
            bgClass={muted ? 'bg-flock-gray-300' : avatarBg(p.user_id)}
          />
          <span
            className={clsx(
              'mt-1 w-full truncate text-center text-[10px]',
              muted ? 'text-flock-navy-300' : 'text-flock-navy-600'
            )}
          >
            {firstName(p.user_name)}
          </span>
        </div>
      ))}
    </div>
  );
}

function SmallRow({ people }: { people: EventRsvp[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {people.map((p) => (
        <span
          key={p.id}
          className="flex items-center gap-1.5 rounded-full bg-flock-navy-50 py-0.5 pl-0.5 pr-2.5 text-xs text-flock-navy-700"
        >
          <Avatar name={p.user_name} sizeClass="h-6 w-6" textClass="text-[9px]" bgClass={avatarBg(p.user_id)} />
          {firstName(p.user_name)}
        </span>
      ))}
    </div>
  );
}

interface Props {
  eventId: string;
  /** Pin requests to the event's org (cross-org calendar). */
  organizationId?: string;
  rsvps: EventRsvp[];
  counts: RsvpCounts;
  /** Tighter layout for the slide-over. */
  compact?: boolean;
  /** Show the Nudge button — caller decides (leader in the event's org + upcoming). */
  canNudge?: boolean;
}

/**
 * The RSVP "face wall": Going as an avatar grid, Maybe / Can't as smaller
 * rows, and non-responders collapsed into an overlapping avatar stack with a
 * leader-only Nudge action (emails one-tap RSVP links to non-responders only).
 */
export default function RsvpList({
  eventId,
  organizationId,
  rsvps,
  counts,
  compact,
  canNudge = true,
}: Props) {
  const { showToast } = useToast();
  const [awaitingOpen, setAwaitingOpen] = useState(false);
  const [nudged, setNudged] = useState(false);

  const going = rsvps.filter((r) => r.status === 'yes');
  const maybe = rsvps.filter((r) => r.status === 'maybe');
  const declined = rsvps.filter((r) => r.status === 'no');
  const awaiting = rsvps.filter((r) => r.status === 'not_responded');
  const awaitingCount = counts.not_responded;

  const nudgeMutation = useMutation({
    mutationFn: () =>
      eventsService.sendReminder(eventId, { audience: 'non_responders', organizationId }),
    onSuccess: (data) => {
      setNudged(true);
      const n = data.sentCount ?? awaitingCount;
      showToast(`Nudge sent to ${n} ${n === 1 ? 'person' : 'people'}`);
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      showToast(
        status === 403
          ? 'You can only nudge events for groups you lead.'
          : "Couldn't send the nudge. Try again.",
        'error'
      );
    },
  });

  const handleNudge = () => {
    if (nudgeMutation.isPending || nudged) return;
    const n = awaitingCount;
    if (!window.confirm(`Email ${n} ${n === 1 ? 'person' : 'people'} who haven't replied?`)) return;
    nudgeMutation.mutate();
  };

  const hasAnyResponse = going.length + maybe.length + declined.length > 0;

  return (
    <div className={clsx('space-y-4', compact && 'space-y-3')}>
      {!hasAnyResponse && awaitingCount === 0 && (
        <p className="text-sm text-flock-navy-400">No responses yet.</p>
      )}

      {going.length > 0 && (
        <section>
          <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-green-700">
            Going · {going.length}
          </h3>
          <FaceGrid people={going} compact={compact} />
        </section>
      )}

      {maybe.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
            Maybe · {maybe.length}
          </h3>
          <SmallRow people={maybe} />
        </section>
      )}

      {declined.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-red-700">
            Can&apos;t go · {declined.length}
          </h3>
          <SmallRow people={declined} />
        </section>
      )}

      {awaitingCount > 0 && (
        <section className="border-t border-flock-gray-200 pt-3">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => setAwaitingOpen((o) => !o)}
              aria-expanded={awaitingOpen}
              className="group flex min-w-0 items-center gap-2 text-left"
            >
              <span className="flex shrink-0">
                {awaiting.slice(0, 3).map((p, i) => (
                  <Avatar
                    key={p.id}
                    name={p.user_name}
                    sizeClass="h-7 w-7"
                    textClass="text-[9px]"
                    bgClass="bg-flock-gray-300"
                    className={clsx('ring-2 ring-white', i > 0 && '-ml-2')}
                  />
                ))}
                {awaiting.length > 3 && (
                  <span className="-ml-2 flex h-7 w-7 items-center justify-center rounded-full bg-flock-navy-100 text-[9px] font-semibold text-flock-navy-500 ring-2 ring-white">
                    +{awaiting.length - 3}
                  </span>
                )}
              </span>
              <span className="truncate text-xs text-flock-navy-500 group-hover:text-flock-navy-700">
                {awaitingCount} awaiting reply
              </span>
              <ChevronDownIcon
                className={clsx(
                  'h-3.5 w-3.5 shrink-0 text-flock-navy-400 transition-transform',
                  awaitingOpen && 'rotate-180'
                )}
              />
            </button>

            {canNudge && (
              <button
                onClick={handleNudge}
                disabled={nudgeMutation.isPending || nudged}
                className={clsx(
                  'flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition',
                  nudged
                    ? 'bg-flock-navy-50 text-flock-navy-400'
                    : 'bg-flock-navy-600 text-white hover:bg-flock-navy-700 disabled:opacity-60'
                )}
              >
                <BellAlertIcon className="h-3.5 w-3.5" />
                {nudged ? 'Nudged just now' : nudgeMutation.isPending ? 'Nudging…' : 'Nudge'}
              </button>
            )}
          </div>

          {awaitingOpen && awaiting.length > 0 && (
            <div className="mt-3">
              <FaceGrid people={awaiting} compact={compact} muted />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
